import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SpeechRecognitionService } from '../../../../core/services/speech-recognition.service';
import { QuickPlayService } from '../../services/quick-play.service';

declare var SpeechRecognition: any;
declare var webkitSpeechRecognition: any;

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onstart: () => void;
  onresult: (event: SpeechRecognitionEvent) => void;
  onerror: (event: SpeechRecognitionErrorEvent) => void;
  onend: () => void;
  start(): void;
  stop(): void;
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

export enum VoiceProcessStatus {
  READY = 'ready',
  LISTENING = 'listening',
  TRANSCRIBING = 'transcribing',
  GETTING_INTENT = 'getting_intent',
  GENERATING_PROMPT = 'generating_prompt',
  WAITING_AI_RESPONSE = 'waiting_ai_response',
  CREATING_PLAYLIST = 'creating_playlist',
  COMPLETED = 'completed',
  ERROR = 'error'
}

export interface VoiceDialogData {
  autoStart?: boolean;
}

@Component({
  selector: 'app-voice-speech-dialog',
  templateUrl: './voice-speech-dialog.component.html',
  styleUrls: ['./voice-speech-dialog.component.scss']
})
export class VoiceSpeechDialogComponent implements OnInit, OnDestroy {
  VoiceProcessStatus = VoiceProcessStatus;
  
  speechSupported = false;
  currentStatus = VoiceProcessStatus.READY;
  interimTranscript = '';
  finalTranscript = '';
  statusMessage = 'Initializing...';
  isActuallyListening = false;
  
  private recognition: SpeechRecognition | null = null;
  private timeoutId: any;
  private silenceTimeoutId: any;
  private lastFinalResult = '';
  
  constructor(
    public dialogRef: MatDialogRef<VoiceSpeechDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: VoiceDialogData,
    private speechService: SpeechRecognitionService,
    private quickPlayService: QuickPlayService,
    private snackBar: MatSnackBar
  ) {}
  
  async ngOnInit() {
    this.speechSupported = this.speechService.isSupported();
    
    if (this.speechSupported) {
      const hasPermission = await this.speechService.requestPermission();
      
      if (hasPermission) {
        this.initializeSpeechRecognition();
        
        if (this.data.autoStart) {
          setTimeout(() => this.startListening(), 500);
        }
      } else {
        this.setStatus(VoiceProcessStatus.ERROR, 'Microphone permission is required');
      }
    } else {
      this.setStatus(VoiceProcessStatus.ERROR, 'Speech recognition not supported');
    }
  }
  
  ngOnDestroy() {
    this.cleanup();
  }
  
  private cleanup() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    if (this.silenceTimeoutId) {
      clearTimeout(this.silenceTimeoutId);
    }
    if (this.recognition) {
      this.recognition.stop();
    }
  }
  
  private initializeSpeechRecognition() {
    try {
      this.recognition = this.speechService.createRecognition({
        continuous: true,
        interimResults: true,
        language: 'en-US'
      });
      
      this.recognition.onstart = () => {
        this.isActuallyListening = true;
        this.setStatus(VoiceProcessStatus.LISTENING, 'Speak now...');
        this.lastFinalResult = '';
      };
      
      this.recognition.onresult = (event: SpeechRecognitionEvent) => {
        this.processResults(event);
      };
      
      this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        this.handleError(event);
      };
      
      this.recognition.onend = () => {
        if (this.currentStatus === VoiceProcessStatus.LISTENING) {
          this.processTranscript();
        }
      };
      
    } catch (error) {
      console.error('Failed to initialize speech recognition:', error);
      this.setStatus(VoiceProcessStatus.ERROR, 'Failed to initialize speech recognition');
    }
  }
  
  startListening() {
    if (!this.speechSupported || !this.recognition) {
      return;
    }
    
    this.clearTranscripts();
    this.isActuallyListening = false;
    
    // Show initializing status while starting
    this.setStatus(VoiceProcessStatus.READY, 'Starting microphone...');
    
    try {
      this.recognition.start();
      
      // Reduced timeout from 15s to 8s for better responsiveness
      this.timeoutId = setTimeout(() => {
        this.stopListening();
      }, 8000);
      
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      this.handleError(error);
    }
  }
  
  private stopListening() {
    this.cleanup();
    
    if (this.currentStatus === VoiceProcessStatus.LISTENING) {
      this.processTranscript();
    }
  }
  
  private processResults(event: SpeechRecognitionEvent) {
    let interim = '';
    let final = '';
    
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i];
      const transcript = result[0].transcript;
      const confidence = result[0].confidence;
      
      // Check if this is truly a final result (confidence > 0)
      // On mobile browsers, interim results are sometimes marked as final but have confidence = 0
      const isTrueFinal = result.isFinal && (confidence === undefined || confidence > 0);
      
      if (isTrueFinal) {
        final += transcript;
      } else {
        interim += transcript;
      }
    }
    
    this.interimTranscript = interim;
    if (final && final.trim()) {
      // Only add final text if it's different from the last final result (debouncing)
      if (final.trim() !== this.lastFinalResult.trim()) {
        this.finalTranscript += (this.finalTranscript ? ' ' : '') + final.trim();
        this.lastFinalResult = final.trim();
      }
    }
    
    if (interim || final) {
      
      // Reset silence timer when we get speech
      if (this.silenceTimeoutId) {
        clearTimeout(this.silenceTimeoutId);
      }
      
      // Set up silence detection - if no speech for 2 seconds after getting some speech, stop listening
      if (final.trim()) {
        this.silenceTimeoutId = setTimeout(() => {
          if (this.currentStatus === VoiceProcessStatus.LISTENING) {
            this.stopListening();
          }
        }, 2000);
      }
    }
  }
  
  private processTranscript() {
    if (!this.finalTranscript.trim()) {
      this.setStatus(VoiceProcessStatus.ERROR, 'No speech detected');
      return;
    }
    
    this.processCommand(this.finalTranscript);
  }
  
  private async processCommand(command: string) {
    try {
      this.setStatus(VoiceProcessStatus.TRANSCRIBING, 'Processing speech...');
      
      await new Promise(resolve => setTimeout(resolve, 200));
      
      this.setStatus(VoiceProcessStatus.GETTING_INTENT, 'Understanding your request...');
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      this.setStatus(VoiceProcessStatus.GENERATING_PROMPT, 'Generating playlist prompt...');
      
      await new Promise(resolve => setTimeout(resolve, 400));
      
      this.setStatus(VoiceProcessStatus.WAITING_AI_RESPONSE, 'Waiting for AI response...');
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      this.setStatus(VoiceProcessStatus.CREATING_PLAYLIST, 'Creating your playlist...');
      
      await this.quickPlayService.processVoiceCommand(command);
      
      this.setStatus(VoiceProcessStatus.COMPLETED, `Successfully created playlist: "${command}"`);
      
      setTimeout(() => {
        this.dialogRef.close({ success: true, command, playlistName: 'Dynamic Playlist' });
      }, 1500);
      
    } catch (error) {
      console.error('Voice command processing error:', error);
      this.setStatus(VoiceProcessStatus.ERROR, 'Failed to process voice command');
    }
  }
  
  private setStatus(status: VoiceProcessStatus, message: string) {
    this.currentStatus = status;
    this.statusMessage = message;
  }
  
  private clearTranscripts() {
    this.interimTranscript = '';
    this.finalTranscript = '';
    this.lastFinalResult = '';
  }
  
  private handleError(error: any) {
    console.error('Speech recognition error:', error);
    
    let message = 'Voice recognition failed';
    
    if (error.error === 'not-allowed') {
      message = 'Microphone permission denied';
    } else if (error.error === 'no-speech') {
      message = 'No speech detected - try again';
    } else if (error.error === 'audio-capture') {
      message = 'Microphone not available';
    } else if (error.error === 'network') {
      message = 'Network error - check your connection';
    }
    
    this.setStatus(VoiceProcessStatus.ERROR, message);
  }
  
  get transcript(): string {
    return this.finalTranscript + this.interimTranscript;
  }
  
  getStatusIcon(): string {
    switch (this.currentStatus) {
      case VoiceProcessStatus.READY:
        return 'mic';
      case VoiceProcessStatus.LISTENING:
        return 'hearing';
      case VoiceProcessStatus.TRANSCRIBING:
        return 'sync';
      case VoiceProcessStatus.GETTING_INTENT:
        return 'psychology';
      case VoiceProcessStatus.GENERATING_PROMPT:
        return 'auto_awesome';
      case VoiceProcessStatus.WAITING_AI_RESPONSE:
        return 'cloud_sync';
      case VoiceProcessStatus.CREATING_PLAYLIST:
        return 'playlist_add';
      case VoiceProcessStatus.COMPLETED:
        return 'check_circle';
      case VoiceProcessStatus.ERROR:
        return 'error';
      default:
        return 'mic';
    }
  }
  
  getStatusColor(): string {
    switch (this.currentStatus) {
      case VoiceProcessStatus.READY:
        return 'primary';
      case VoiceProcessStatus.LISTENING:
        return 'accent';
      case VoiceProcessStatus.TRANSCRIBING:
      case VoiceProcessStatus.GETTING_INTENT:
      case VoiceProcessStatus.GENERATING_PROMPT:
      case VoiceProcessStatus.CREATING_PLAYLIST:
        return 'primary';
      case VoiceProcessStatus.COMPLETED:
        return 'primary';
      case VoiceProcessStatus.ERROR:
        return 'warn';
      default:
        return 'primary';
    }
  }
  
  isProcessing(): boolean {
    return [
      VoiceProcessStatus.LISTENING,
      VoiceProcessStatus.TRANSCRIBING,
      VoiceProcessStatus.GETTING_INTENT,
      VoiceProcessStatus.GENERATING_PROMPT,
      VoiceProcessStatus.WAITING_AI_RESPONSE,
      VoiceProcessStatus.CREATING_PLAYLIST
    ].includes(this.currentStatus);
  }
  
  canRetry(): boolean {
    return this.currentStatus === VoiceProcessStatus.ERROR || this.currentStatus === VoiceProcessStatus.READY;
  }
  
  retry() {
    this.setStatus(VoiceProcessStatus.READY, 'Ready to listen');
    this.clearTranscripts();
    this.startListening();
  }
  
  close() {
    this.dialogRef.close({ success: false });
  }
}